import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  UserIcon,
  LockClosedIcon,
  BellIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

const Settings = () => {
  const { t } = useTranslation();
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateProfile(profileData);
    } catch (error) {
      // Error handled by auth context
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t("settings.password.errors.mismatch"));
      return;
    }

    setIsLoading(true);
    try {
      await changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      // Error handled by auth context
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: "profile", name: t("settings.tabs.profile"), icon: UserIcon },
    { id: "password", name: t("settings.tabs.password"), icon: LockClosedIcon },
    {
      id: "notifications",
      name: t("settings.tabs.notifications"),
      icon: BellIcon,
    },
    {
      id: "preferences",
      name: t("settings.tabs.preferences"),
      icon: Cog6ToothIcon,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-white">{t("settings.title")}</h1>
      </div>

      <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
        <div className="flex border-b border-dark-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-3 text-sm font-medium ${
                activeTab === tab.id
                  ? "text-primary-500 border-b-2 border-primary-500"
                  : "text-dark-300 hover:text-white"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === "profile" && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              <div>
                <label className="form-label">
                  {t("settings.profile.fullName")}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={profileData.name}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  {t("settings.profile.email")}
                </label>
                <input
                  type="email"
                  className="form-input"
                  value={user?.email || ""}
                  disabled
                />
                <p className="text-sm text-dark-400 mt-1">
                  {t("settings.profile.emailNote")}
                </p>
              </div>
              <div>
                <label className="form-label">
                  {t("settings.profile.phone")}
                </label>
                <input
                  type="tel"
                  className="form-input"
                  value={profileData.phone}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                />
              </div>
              <div>
                <label className="form-label">
                  {t("settings.profile.role")}
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={user?.role || ""}
                  disabled
                />
                <p className="text-sm text-dark-400 mt-1">
                  {t("settings.profile.roleNote")}
                </p>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary"
                >
                  {isLoading
                    ? t("settings.profile.updating")
                    : t("settings.profile.updateButton")}
                </button>
              </div>
            </form>
          )}

          {activeTab === "password" && (
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div>
                <label className="form-label">
                  {t("settings.password.current")}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      currentPassword: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  {t("settings.password.new")}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      newPassword: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div>
                <label className="form-label">
                  {t("settings.password.confirm")}
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  required
                />
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-primary"
                >
                  {isLoading
                    ? t("settings.password.changing")
                    : t("settings.password.changeButton")}
                </button>
              </div>
            </form>
          )}

          {activeTab === "notifications" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white">
                    {t("settings.notifications.email.title")}
                  </h3>
                  <p className="text-sm text-dark-400">
                    {t("settings.notifications.email.description")}
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                  defaultChecked
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white">
                    {t("settings.notifications.taskReminders.title")}
                  </h3>
                  <p className="text-sm text-dark-400">
                    {t("settings.notifications.taskReminders.description")}
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                  defaultChecked
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white">
                    {t("settings.notifications.maintenance.title")}
                  </h3>
                  <p className="text-sm text-dark-400">
                    {t("settings.notifications.maintenance.description")}
                  </p>
                </div>
                <input
                  type="checkbox"
                  className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                  defaultChecked
                />
              </div>
            </div>
          )}

          {activeTab === "preferences" && (
            <div className="space-y-6">
              <div>
                <label className="form-label">
                  {t("settings.preferences.language")}
                </label>
                <select className="form-input">
                  <option value="en">
                    {t("settings.preferences.languages.english")}
                  </option>
                  <option value="de">
                    {t("settings.preferences.languages.german")}
                  </option>
                  <option value="es">
                    {t("settings.preferences.languages.spanish")}
                  </option>
                </select>
              </div>
              <div>
                <label className="form-label">
                  {t("settings.preferences.timezone")}
                </label>
                <select className="form-input">
                  <option value="UTC">UTC</option>
                  <option value="Europe/Berlin">
                    {t("settings.preferences.timezones.berlin")}
                  </option>
                  <option value="America/New_York">
                    {t("settings.preferences.timezones.eastern")}
                  </option>
                  <option value="America/Los_Angeles">
                    {t("settings.preferences.timezones.pacific")}
                  </option>
                </select>
              </div>
              <div>
                <label className="form-label">
                  {t("settings.preferences.defaultView")}
                </label>
                <select className="form-input">
                  <option value="table">
                    {t("settings.preferences.views.table")}
                  </option>
                  <option value="card">
                    {t("settings.preferences.views.card")}
                  </option>
                  <option value="kanban">
                    {t("settings.preferences.views.kanban")}
                  </option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
